import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { ColDef, GridReadyEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import React, { useMemo, useCallback } from 'react'
import { Spin } from 'antd'
import { Typography } from '@sureco/design-system'
import { OperatorStats } from '../types/PerformanceDashboard'
import GridNoResultsOverlay from 'components/GridNoResultsOverlay/GridNoResultsOverlay'
import { useOperatorPerformance } from '../hooks/useOperatorPerformance'
import { TIMEZONE_PST } from 'helpers/datetime'

dayjs.extend(timezone)

interface PerformanceTableProps {
  selectedBenefitEffectiveDate: string
}

const columnDefs: ColDef<OperatorStats>[] = [
  {
    field: 'assignee',
    headerName: 'Assignee',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'yesterdaySureCoSubmitted',
    headerName: 'Yesterday Submitted',
    flex: 1,
    minWidth: 150,
    cellStyle: { textAlign: 'right' },
    cellRenderer: (params: { value: number }) => {
      return params.value?.toLocaleString() || '0'
    },
  },
  {
    field: 'todaySureCoSubmitted',
    headerName: 'Today Submitted',
    flex: 1,
    minWidth: 150,
    cellStyle: { textAlign: 'right' },
    cellRenderer: (params: { value: number }) => {
      return params.value?.toLocaleString() || '0'
    },
  },
  {
    field: 'thisWeekSureCoSubmitted',
    headerName: 'This Week Submitted',
    flex: 1,
    minWidth: 180,
    cellStyle: { textAlign: 'right' },
    cellRenderer: (params: { value: number }) => {
      return params.value?.toLocaleString() || '0'
    },
  },
  {
    field: 'totalSureCoSubmitted',
    headerName: 'Total Submitted',
    flex: 1,
    minWidth: 150,
    cellStyle: { textAlign: 'right' },
    cellRenderer: (params: { value: number }) => {
      return params.value?.toLocaleString() || '0'
    },
  },
  {
    field: 'totalUnsubmitted',
    headerName: 'Total Application Left',
    flex: 1,
    minWidth: 180,
    cellStyle: { textAlign: 'right' },
    cellRenderer: (params: { value: number }) => {
      return params.value?.toLocaleString() || '0'
    },
  },
]

export const PerformanceTable: React.FC<PerformanceTableProps> = ({
  selectedBenefitEffectiveDate,
}) => {
  const { data, isLoading, error } = useOperatorPerformance({
    effectiveDate: selectedBenefitEffectiveDate,
    timezone: TIMEZONE_PST,
  })

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      suppressMenu: true,
    }),
    []
  )

  const onGridReady = useCallback((params: GridReadyEvent<OperatorStats>) => {
    // Set default sort by Total Application Left (descending)
    params.api.applyColumnState({
      state: [{ colId: 'totalUnsubmitted', sort: 'desc' }],
      defaultState: { sort: null },
    })
  }, [])

  if (error) {
    return (
      <div>
        <h2>Performance Table</h2>
        <div>Error loading performance data. Please try again later.</div>
      </div>
    )
  }

  return (
    <div>
      <Typography as="h2" size={20} bold>
        Performance Table
      </Typography>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '40px',
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <div
          className="ag-theme-alpine"
          style={{ width: '100%', height: '600px' }}
        >
          <AgGridReact
            columnDefs={columnDefs}
            rowData={data?.operators || []}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            noRowsOverlayComponent={GridNoResultsOverlay}
            gridOptions={{
              sortingOrder: ['desc', 'asc', null],
            }}
          />
        </div>
      )}
    </div>
  )
}
